<template>
  <div>
    <TopNav/>
    <div class="recorder-page ">
      <h4>Recording Session 
        <i class="fa-solid fa-circle " style="font-size: 10px;"
          :class="{'text-success': streamConnected ,'text-danger': !streamConnected }">
        </i>
      </h4>
      <p class="mx-4">Record patient encounters for AI processing and SOAP note generation</p>
      <div class="row data mx-4">
        <div class="col-12 col-lg-8">
          <div class="container mt-3 card p-4">
            <div>
              <h3><i class="fa-solid fa-microphone"  :class="{
                    'text-success pulse': isRecording ,
                    'text-warning pulse': isPaused,
                    'text-muted': !isRecording 
                  }"></i> Audio Recording </h3>
              <p>Control your recording session  </p>
            </div>
            <div class="text-center">
              <div class="counter mb-3">{{ formatTime(counter) }}</div>
              <AudioWaveform :active="isRecording && !isPaused" :stream="visualStream"/>
              <button class="btn btn-lg btn-primary mic-button" @click="toggleRecording">
                <i :class="micIcon"></i>
              </button>
              <button class="btn btn-danger ms-3" @click="stopRecord" :disabled="!isRecording">
                <i class="fa-solid fa-stop"></i>
              </button>
              <div class="controls mt-4">
                <button class="btn btn-warning me-2" @click="saveDraft(transcription)"
                  :disabled="streamConnected">
                  <span v-if="isSending || isTranscribing" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  <span v-else>Draft</span></button>
                <button class="btn btn-primary" @click="sendToSofia(transcription)" :disabled="streamConnected">
                  <span v-if="isSending || isTranscribing" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  <span v-else>Send to Sofia</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      <div class="col-12 col-lg-4 mt-3">
        <div class="card p-3">
          <h4>Current Session</h4>
          <p>Patient: <strong>{{ patientName }}</strong></p>
          <p>Patient Id: <strong>#{{ patientId }}</strong></p>
          <p>Date of Birth : <strong>{{DOB  }}</strong></p>
          <p>Past Encounters Number: <strong>{{encounterNumber  }}</strong></p>
          <p>Encounters Mode: <strong>{{encounterMode  }}</strong></p>
        </div>
        <div class="card p-3 mt-3">
          <h4><i class="fa-solid fa-tower-broadcast"></i> AI Processing</h4>
          <div class="card mt-3 d-flex flex-row p-3 align-items-center">
            <span class="me-2">
              <i class="fa-solid fa-circle"
                :class="{
                  'text-success pulse': startTranscribing || isTranscribing,
                  'text-muted': !startTranscribing && !isTranscribing
                }">
              </i>
            </span>
            <div >
              <h6 class="mb-0">Real-time Transcription</h6>
              <p class="mb-0">Active during recording</p>
            </div>
          </div>
        </div>
      </div>
      </div>

      <!-- Modal -->
      <div v-if="showConfirm" class="modal-overlay">
        <div class="modal-content">
          <h3>Are you sure you want to leave?</h3>
          <p>You have unsent transcription or draft. If you leave, your progress will be lost.</p>
          <div class="modal-buttons">
            <button @click="confirmLeave" class="btn btn-danger">Leave</button>
            <button @click="cancelLeave" class="btn btn-secondary">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed,onBeforeUnmount, onMounted } from 'vue';
import { useRecorder } from '../utils/useRecord3';
import { useStore } from 'vuex';
import { addToMessagesSofiaPlus } from '../utils/chatMessages';
import { useRouter, useRoute } from 'vue-router';
import { useToast } from 'vue-toastification';
import TopNav from '../layouts/topNav.vue'
import { getPatientByIdService} from "@/services/api/patientService";
import { formatTime} from "@/utils/commonFunctions";
import AudioWaveform from '../components/AudioWaveform.vue';

const router = useRouter(); 
const route = useRoute();
const toast = useToast();
const store = useStore();

const language = ref('en');
const { isRecording,transcription, startRecording, stopRecording,isTranscribing,pauseRecording,resumeRecording,isPaused, visualStream} = useRecorder(language, (errorMsg) => {
  toast.error(errorMsg, { timeout: 6000 },
    isRecording.value = false
  )
})
const counter = ref(0);
const micIcon = ref('fa-solid fa-microphone');
const interval = ref(null);
const patientName = ref();
const DOB = ref('');
const DOC = computed(() => store.state.dOC);
const ehrOrgName =  route.query.ehrOrgName|| {};
const ehrPatientId =  route.query.ehrPatientId|| {};
const DOS = computed(() => store.state.dOS);
const isSending = ref(false);
const showConfirm = ref(false); 
const isTranscription =ref(false); 
const patientId = computed(() => store.state.patientId);
const startTranscribing = ref(false)
const encounterNumber = ref('')
const encounterMode = ref(localStorage.getItem('encounterMode'));

// eslint-disable-next-line no-unused-vars, no-undef
const props = defineProps({
  socket: Object,
  sendMessage: Function,
  handleMessage: Function,
});

// eslint-disable-next-line no-undef
const emit = defineEmits(['send']);
const streamConnected = computed(() => store.state.websocket.streamConnected);

watch(streamConnected, (newVal, oldVal) => {
  if (newVal) {
    console.log('✅ WebSocket connected')
  } else if (oldVal) {
    console.log('⚠️ WebSocket disconnected')
  }
})
watch([isRecording, isPaused], ([recording, paused]) => {
  if (!recording) {
    // Stopped
    micIcon.value = 'fa-solid fa-microphone'
    startTranscribing.value = false
    clearInterval(interval.value)
    interval.value = null
  } else if (paused) {
    // Paused
    micIcon.value = 'fa-solid fa-play' 
    startTranscribing.value = false 
    clearInterval(interval.value) 
  } else {
    // Recording
    micIcon.value = 'fa-solid fa-pause'
    startTranscribing.value = true
    if (!interval.value) {
        interval.value = setInterval(() => { counter.value += 1 }, 1000)
    }
  }
})

const toggleRecording = async () => {
  if (!isRecording.value) {
    startRecording(encounterMode.value);
    startTimer()
  } else {
    if (isPaused.value) {
      await resumeRecording();
      startTimer()
    } else {
      await pauseRecording();
      stopTimer()
    }
  }
};
const stopRecord =() =>{
  stopRecording();
  stopTimer()
}
const startTimer = () => {
  if (!interval.value) {
    interval.value = setInterval(() => counter.value++, 1000)
  }
}

const stopTimer = () => {
  clearInterval(interval.value)
  interval.value = null
}
watch(transcription, (newValue) => {
  if (newValue) {
    isSending.value = false; 
  }
});

const saveDraft = (messageText) => {
  const trimmedMessage = messageText.trim();
  if (trimmedMessage.length > 0) {
    isSending.value = true;  
    const messagePayload = {
      message: trimmedMessage,
      is_draft: 1,
      category_id: 'conversation between patient and doctor',
      chat_id: 0,
      message_id: '12',
      duration: formatTime(counter.value),
      encounter_type: encounterMode.value,
    };
    toast.success("Draft saved");
    store.dispatch('websocket/sendMessage', messagePayload);
    router.push({ path: '/' });
  }
};
onMounted(()=>{
  getPatientById()
})
const sendToSofia = async (messageText) => {
  const trimmedMessage = messageText.trim();
  if (trimmedMessage.length > 0) {
    isSending.value = true; 
    const messagePayload = {
      message: trimmedMessage,
      is_draft: 0,
      category_id: 'conversation between patient and doctor',
      chat_id: 0,
      message_id: '12',
      patian_name: patientName.value,
      date_of_birth : DOB.value,
      date_of_complain: DOC.value,
      date_of_service:DOS.value,
      patient_ehr_id:ehrPatientId ,
      organization_name:ehrOrgName ,
      duration: formatTime(counter.value),
      encounter_type: encounterMode.value,
    };
    props.sendMessage(messagePayload);
    addToMessagesSofiaPlus({
      id: 0,
      topicId: 0,
      body: transcription.value.trim(),
      isMe: true,
      time: new Date(),
      topicName: 'conversation between patient and doctor',
    }, true, true);
    store.commit('navigation/triggerRefreshTopics')
    emit('send');
  }
};

watch(transcription, (newValue) => {
  if (newValue !== '') {
    isTranscription.value = true;  
  } else {
    isTranscription.value = false;
  }
});

const handleBeforeUnload = (event) => {
  if (isTranscription.value || isRecording.value) {
    const message = 'You have unsaved data. Are you sure you want to leave?';
    event.returnValue = message; 
    return message; 
  }
};
const getPatientById = async () => {
  const result = await getPatientByIdService(patientId.value);
  DOB.value = result.date_of_birth;
  patientName.value = result.name
  encounterNumber.value = result.number_of_encounters
};

window.addEventListener('beforeunload', handleBeforeUnload);


// Clean up the event listener when the component is unmounted
onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload);

});

// // Route leave confirmation modal
// router.beforeEach((to, from, next) => {
//   if (!transcription.value || isSending.value) {
//     next();
//   } else {
//     showConfirm.value = true;
//     resolveLeave = next;
//   }
// });

// const confirmLeave = () => {
//   showConfirm.value = false;
//   resolveLeave();
// };

// const cancelLeave = () => {
//   showConfirm.value = false;
// };

onBeforeUnmount(() => {
  if (!transcription.value) return;

  const answer = "You have unsent transcription or draft. Are you sure you want to leave?";
  if (!answer) {
    event.preventDefault();
  }
});

</script>

<style scoped>
.recorder-page {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  margin-top: 0px;
}

.data{
  width: 80%;
}
.counter {
  font-size: 2rem;
  margin-bottom: 20px;
}

.mic-button {
  font-size: 4rem;
  width: 10rem;
  height: 10rem;
  border: none;
  background-color: transparent;
  cursor: pointer;
  outline: none;
  color: white;
  background-color: #518DB1;
  border-radius: 50%;
}

.controls {
  display: flex;
  justify-content: center;
}

.nav-header {
  background-color: #518DB1;
  left: unset;  
}
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal-content {
  background: white;
  padding: 20px;
  border-radius: 10px;
  text-align: center;
  width:50%;
}

.modal-buttons {
  margin-top: 20px;
}
.pulse {
  animation: pulse 1s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50%      { transform: scale(1.2); opacity: 0.7; }
}


@media (max-width: 576px) {
  .mic-button {
    font-size: 3rem;
  }

  .counter {
    font-size: 1.5rem;
  }
  .recorder-page {
    margin-top: 320px;
  }
  .data{
    width: 100%;
  }
}
</style>
